import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { apiFetch } from "@/lib/api";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  FiCalendar,
  FiSettings,
  FiPlus,
  FiTrash2,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiUser,
  FiCrop,
  FiUpload,
  FiFolder,
} from "react-icons/fi";
import { LuAudioLines, LuFilm, LuBrainCircuit, LuUser } from "react-icons/lu";
import { RiAiGenerate2 } from "react-icons/ri";
import { MdAspectRatio, MdHd } from "react-icons/md";
import { IoIosRefresh } from "react-icons/io";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/shadcn/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/dropdown-menu";
import { Switch } from "@/components/ui/shadcn/switch";
import { Input } from "@/components/ui/shadcn/input";
import Button from "@/components/ui/Button";
import useFileUpload from "@/hooks/useFileUpload";
import useKagamiBridge from "@/hooks/useKagamiBridge";
import { useModal } from "@/contexts/ModalContext";
import { useShow } from "@/contexts/ShowContext";
import ScheduleModal from "@/components/modals/ScheduleModal";
import VoiceSettingsModal from "@/components/modals/VoiceSettingsModal";
import AssetPreviewModal from "@/components/modals/AssetPreviewModal";
import AssetsBrowserModal from "@/components/modals/AssetsBrowserModal";
import ImagePreviewModal from "@/components/modals/ImagePreviewModal";
import VideoPreviewModal from "@/components/modals/VideoPreviewModal";
import AudioPreviewModal from "@/components/modals/AudioPreviewModal";
import {
  ModelSelectorDialog,
  GENERATION_MEDIA_PREFIX,
} from "@/components/AiModelEditor";
import { GENERATION_DRAG_TYPE } from "@/components/storyboard/StoryboardGenerations";
import {
  ASSET_DRAG_MIME,
  ASSET_MEDIA_PREFIX,
} from "@/components/AssetsEditor/constants";
import { Checkbox } from "@/components/ui/shadcn/checkbox";
import { toast } from "sonner";

const VISUAL_TYPES = [
  "image",
  "video",
  "audio",
  "elements",
  "video_elements",
  "audio_elements",
];
const SETTING_TYPES = [
  "options",
  "boolean",
  "character",
  "voice_settings",
  "number",
];

function getGenerationDraggedMediaType(types) {
  const match = types.find((t) => t.startsWith(GENERATION_MEDIA_PREFIX));
  return match ? match.slice(GENERATION_MEDIA_PREFIX.length) : null;
}

/**
 * Returns every `mediaType` advertised on the current asset drag. Multi-asset
 * drags from AssetsEditor set one `assetMediaDragType(mt)` marker per unique
 * mediaType in the selection (see `AssetItem.handleDragStart`), so reading
 * them all lets a typed drop target accept a drag that contains *some*
 * matching asset and filter the rest at drop time.
 */
function getAssetDraggedMediaTypes(types) {
  return types
    .filter((t) => t.startsWith(ASSET_MEDIA_PREFIX))
    .map((t) => t.slice(ASSET_MEDIA_PREFIX.length));
}

function hasMatchingFileItem(dataTransfer, mediaType) {
  const items = [...(dataTransfer?.items || [])];
  if (items.length === 0) return true;
  return items.some(
    (it) =>
      it.kind === "file" && (!it.type || it.type.startsWith(`${mediaType}/`)),
  );
}

/**
 * Mapping from "loose-matched keywords" → icon component. Order matters: the
 * first entry whose keyword is found in the normalized input name wins. New
 * options names should grow this list (or alias an existing keyword).
 *
 * Distinct names observed in the AiModel collection at the time of writing:
 *   Aspect ratio, Aspect Ratio, aspect_ratio, Audio, Character Orientation,
 *   Duration, FPS, Frame, Model, Resolution, Retake Mode, Size.
 */
const OPTIONS_ICON_MAP = [
  ["aspectratio", MdAspectRatio],
  ["size", MdAspectRatio],
  ["resolution", MdHd],
  ["duration", FiClock],
  ["fps", LuFilm],
  ["retake", IoIosRefresh],
  ["orientation", FiUser],
  ["frame", FiCrop],
  ["audio", LuAudioLines],
  ["model", LuBrainCircuit],
];

/**
 * Returns a React element for an `options`-type input's icon, picked via a
 * loose (case/separator-insensitive substring) match on the input's display
 * name. Returns `null` when no keyword matches so callers can render nothing.
 *
 * @param {string|undefined|null} name - input.name from the model schema
 * @param {string} [className='w-3 h-3'] - className applied to the icon
 * @returns {import('react').ReactElement|null}
 */
export function getOptionsIcon(name, className = "w-3 h-3") {
  if (!name) return null;
  const normalized = String(name)
    .toLowerCase()
    .replace(/[\s_\-]/g, "");
  for (const [keyword, Icon] of OPTIONS_ICON_MAP) {
    if (normalized.includes(keyword)) return <Icon className={className} />;
  }
  return null;
}

function elementsMediaType(inputType) {
  if (inputType === "elements") return "image";
  if (inputType === "video_elements") return "video";
  if (inputType === "audio_elements") return "audio";
  return inputType;
}

/** Inverse of `elementsMediaType` — preferred multi-value input type per media. */
const ELEMENTS_INPUT_TYPE_FOR = {
  image: "elements",
  video: "video_elements",
  audio: "audio_elements",
};

const ELEMENTS_INPUT_TYPES = new Set(Object.values(ELEMENTS_INPUT_TYPE_FOR));

/**
 * A picker value can be either a remote URL string (already-uploaded asset) or
 * a "pending upload" wrapper holding a local File. We delay the actual upload
 * until the user clicks Generate so files don't get pushed to storage for
 * generations that get abandoned.
 */
function isPendingUpload(v) {
  return !!v && typeof v === "object" && v.__pending === true;
}

function makePendingUpload(file, mediaType) {
  return {
    __pending: true,
    file,
    previewUrl: URL.createObjectURL(file),
    mediaType,
    name: file.name,
  };
}

/**
 * Returns the URL to render in the thumbnail slot. Prefers `thumbnail_url` for
 * rich values, falls back to the full url; pending uploads use the local blob
 * URL; plain string values are returned as-is (no thumbnail known).
 */
function pickerDisplayUrl(v) {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (isPendingUpload(v)) return v.previewUrl;
  return v.thumbnail_url || v.url || null;
}

/**
 * Returns the dedicated thumbnail URL (only) for a picker value, or null if
 * no thumbnail is known. Used by video pickers to decide between `<img>` (when
 * a thumbnail exists) and `<video>` (when we must render the asset itself).
 */
function pickerThumbnailUrl(v) {
  if (!v) return null;
  if (typeof v === "string") return null;
  if (isPendingUpload(v)) return null;
  return v.thumbnail_url || null;
}

/**
 * Returns the canonical asset URL stored in a picker value. Used at submit
 * time to flatten rich `{ url, thumbnail_url }` values back to a plain URL
 * (pending uploads are handled separately by `uploadPendingAssets`).
 */
function pickerSubmitUrl(v) {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (isPendingUpload(v)) return null;
  return v.url || null;
}

/**
 * Identity key for a picker value, used to dedupe elements-array entries:
 *  - string / rich values → canonical asset URL
 *  - pending uploads → the underlying `File` object (so the same selected
 *    file dropped twice doesn't appear twice in the row)
 * Returns `null` when there is no stable identity yet; callers should treat
 * `null` keys as "not a duplicate" (i.e. always include).
 */
function pickerDedupeKey(v) {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (isPendingUpload(v)) return v.file;
  return v.url || null;
}

/**
 * Filters `incoming` picker values down to those whose `pickerDedupeKey` is
 * not already present in `existing` (and not duplicated within `incoming`
 * itself). Used to keep elements rows free of duplicate assets.
 */
function dedupeAgainst(existing, incoming) {
  const seen = new Set(existing.map(pickerDedupeKey).filter((k) => k != null));
  const out = [];
  for (const v of incoming) {
    const k = pickerDedupeKey(v);
    if (k != null && seen.has(k)) continue;
    if (k != null) seen.add(k);
    out.push(v);
  }
  return out;
}

/**
 * dnd-kit modifier that strips the y-axis from drag transforms so sortable
 * items in a horizontal row don't lift vertically while dragging.
 */
const restrictToHorizontalAxis = ({ transform }) => ({
  ...transform,
  y: 0,
});

/**
 * Clamps a {x, y} translation so the supplied node rect stays inside the
 * supplied bounding rect. Shared helper used by the parent-element and
 * window-edges restrict modifiers.
 */
function clampTransformToRect(transform, nodeRect, boundingRect) {
  if (!nodeRect || !boundingRect) return transform;
  const next = { ...transform };
  if (nodeRect.left + transform.x <= boundingRect.left) {
    next.x = boundingRect.left - nodeRect.left;
  } else if (nodeRect.right + transform.x >= boundingRect.right) {
    next.x = boundingRect.right - nodeRect.right;
  }
  if (nodeRect.top + transform.y <= boundingRect.top) {
    next.y = boundingRect.top - nodeRect.top;
  } else if (nodeRect.bottom + transform.y >= boundingRect.bottom) {
    next.y = boundingRect.bottom - nodeRect.bottom;
  }
  return next;
}

/** dnd-kit modifier preventing the dragging node from leaving the viewport. */
const restrictToWindowEdges = ({ draggingNodeRect, transform, windowRect }) =>
  clampTransformToRect(transform, draggingNodeRect, windowRect);

/**
 * Returns a dnd-kit modifier that prevents the dragging node from leaving the
 * rect of the element pointed to by `containerRef`. We compute the rect lazily
 * on each call so layout changes (resize, scroll) are picked up.
 */
function makeRestrictToContainer(containerRef) {
  return ({ draggingNodeRect, transform }) => {
    const el = containerRef.current;
    if (!el || !draggingNodeRect) return transform;
    return clampTransformToRect(
      transform,
      draggingNodeRect,
      el.getBoundingClientRect(),
    );
  };
}

/**
 * Compact square asset picker. Supports:
 *  - Drop files (stored locally; actual upload deferred until Generate)
 *  - Drop a generation asset (URL captured directly)
 *  - Click empty square to open a menu: Upload (file picker, stored locally as
 *    a pending file) or Browse (pick an existing asset via AssetsBrowserModal)
 *  - Click a populated square to preview the asset in a modal
 *  - Click X to clear
 *
 * `asset` can be either a URL string (already-uploaded asset) or a pending
 * upload wrapper produced by `makePendingUpload`.
 */
function MiniAssetPicker({
  asset,
  mediaType = "image",
  onChange,
  onAdd,
  onPreview,
  sizeClass = "w-16 h-16",
  disabled = false,
  multiSelect = false,
  dragHandleProps,
  resolveAssetById,
  onAssetDrop,
}) {
  const { openModal } = useModal();
  const inputId = useMemo(
    () => `mini-upload-${Math.random().toString(36).slice(2, 10)}`,
    [],
  );
  const [isDragOver, setIsDragOver] = useState(false);
  // Global drag state, used to grey-out non-matching pickers while a relevant
  // drag is in progress (mirrors the AssetPicker pattern in AiModelEditor).
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDragTypeMatch, setIsDragTypeMatch] = useState(false);
  const dragCounter = useRef(0);

  // Whether to accept a drag based purely on the dataTransfer types. We
  // intentionally don't peek at file MIME types here: some browsers don't
  // expose them during dragenter/dragover, and the original AssetPicker
  // accepts any file drag and filters at drop time. We mirror that so file
  // drops never silently get rejected on the dragover phase.
  const acceptsDrag = useCallback(
    (e) => {
      const types = [...e.dataTransfer.types];
      if (types.includes(GENERATION_DRAG_TYPE)) {
        return getGenerationDraggedMediaType(types) === mediaType;
      }
      if (resolveAssetById && types.includes(ASSET_DRAG_MIME)) {
        const dragged = getAssetDraggedMediaTypes(types);
        if (dragged.length === 0) return true;
        if (dragged.includes(mediaType)) return true;
        // Multi-type drags get redistributed across the whole panel, so
        // any picker can accept the drop even when its own type isn't in
        // the selection (the drop handler routes each asset to the best
        // input by mediaType).
        return dragged.length > 1 && !!onAssetDrop;
      }
      if (types.includes("Files")) {
        // Best-effort match for the greyed-out non-match styling; falls
        // back to `true` when item types aren't readable yet.
        return hasMatchingFileItem(e.dataTransfer, mediaType);
      }
      return false;
    },
    [mediaType, resolveAssetById, onAssetDrop],
  );

  // Document-level drag tracking → drives the greyed-out non-match state.
  useEffect(() => {
    let scheduled = false;
    let resetTimeout = null;
    const resetDrag = () => {
      setIsDragActive(false);
      setIsDragTypeMatch(false);
      setIsDragOver(false);
      dragCounter.current = 0;
      if (resetTimeout) {
        clearTimeout(resetTimeout);
        resetTimeout = null;
      }
    };
    const onGlobalDragOver = (e) => {
      const types = [...e.dataTransfer.types];
      const isGen = types.includes(GENERATION_DRAG_TYPE);
      const isAsset = !!resolveAssetById && types.includes(ASSET_DRAG_MIME);
      const isFile = types.includes("Files");
      if (!isGen && !isAsset && !isFile) return;
      let match = false;
      if (isGen) match = getGenerationDraggedMediaType(types) === mediaType;
      else if (isAsset) {
        const dragged = getAssetDraggedMediaTypes(types);
        if (dragged.length === 0) match = true;
        else if (dragged.includes(mediaType)) match = true;
        // Multi-type asset drags will be redistributed at the panel
        // level, so visually invite drops on *every* picker (no
        // greyed-out state) when there's an onAssetDrop handler.
        else match = dragged.length > 1 && !!onAssetDrop;
      } else if (isFile) match = hasMatchingFileItem(e.dataTransfer, mediaType);

      if (resetTimeout) clearTimeout(resetTimeout);
      // Auto-reset shortly after the last dragover; covers cases where
      // dragend/drop don't fire (drop outside window, etc.).
      resetTimeout = setTimeout(resetDrag, 300);
      if (!scheduled) {
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          setIsDragActive(true);
          setIsDragTypeMatch(match);
        });
      }
    };
    // Capture phase so a local handler's stopPropagation (e.g. on the
    // currently-hovered picker) doesn't prevent us from seeing the drag.
    document.addEventListener("dragover", onGlobalDragOver, true);
    document.addEventListener("dragend", resetDrag, true);
    document.addEventListener("drop", resetDrag, true);
    return () => {
      document.removeEventListener("dragover", onGlobalDragOver, true);
      document.removeEventListener("dragend", resetDrag, true);
      document.removeEventListener("drop", resetDrag, true);
      if (resetTimeout) clearTimeout(resetTimeout);
    };
  }, [mediaType, resolveAssetById, onAssetDrop]);

  const handleDragEnter = useCallback(
    (e) => {
      if (!acceptsDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current += 1;
      setIsDragOver(true);
    },
    [acceptsDrag],
  );

  const handleDragOver = useCallback(
    (e) => {
      if (!acceptsDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      // Asset-grid drags use effectAllowed='copyMove'; using 'move' here keeps
      // the cursor consistent with the rest of the grid. Generation/file drags
      // are always 'copy'.
      const types = [...e.dataTransfer.types];
      e.dataTransfer.dropEffect = types.includes(ASSET_DRAG_MIME)
        ? "move"
        : "copy";
    },
    [acceptsDrag],
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragOver(false);
    }
  }, []);

  /**
   * Filters files by mediaType and emits them as pending uploads. Uses
   * `onAdd(values[])` when `multiSelect` is set (so the elements-row "add"
   * picker can append several pickers at once), otherwise emits the first
   * matching file via `onChange`.
   */
  const emitFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);
      const valid = files.filter((f) =>
        (f?.type || "").startsWith(`${mediaType}/`),
      );
      const skipped = files.length - valid.length;
      if (skipped > 0) {
        toast.warning(
          `Skipped ${skipped} file${skipped === 1 ? "" : "s"} — expected ${mediaType}`,
        );
      }
      if (valid.length === 0) return;
      if (multiSelect) {
        const pendings = valid.map((f) => makePendingUpload(f, mediaType));
        if (onAdd) onAdd(pendings);
        else onChange?.(pendings[0]);
      } else {
        onChange?.(makePendingUpload(valid[0], mediaType));
      }
    },
    [mediaType, multiSelect, onAdd, onChange],
  );

  const handleDrop = useCallback(
    (e) => {
      if (!acceptsDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragOver(false);
      const types = [...e.dataTransfer.types];
      if (types.includes(GENERATION_DRAG_TYPE)) {
        const raw = e.dataTransfer.getData(GENERATION_DRAG_TYPE);
        if (!raw) return;
        try {
          const payload = JSON.parse(raw);
          if (payload?.url && payload?.type === mediaType) {
            onChange?.(
              payload.thumbnail_url
                ? { url: payload.url, thumbnail_url: payload.thumbnail_url }
                : payload.url,
            );
          }
        } catch {
          /* ignore */
        }
        return;
      }
      if (resolveAssetById && types.includes(ASSET_DRAG_MIME)) {
        try {
          const ids = JSON.parse(
            e.dataTransfer.getData(ASSET_DRAG_MIME) || "[]",
          );
          const list = Array.isArray(ids) ? ids : [];
          const dragged = list
            .map((id) => resolveAssetById(id))
            .filter(Boolean);
          // Any drag carrying more than one asset is routed through the
          // panel-level distribution handler (see `distributeAssetsToInputs`
          // in `AiModelInput`), so each asset ends up in the best input
          // for its mediaType (e.g. images → elements, video → video_elements)
          // regardless of which picker physically received the drop.
          if (dragged.length > 1 && onAssetDrop) {
            onAssetDrop(dragged);
            return;
          }
          const matching = dragged.filter(
            (a) => a?.mediaType === mediaType && a?.url,
          );
          if (matching.length === 0) {
            if (dragged.length > 0) {
              const got =
                [...new Set(dragged.map((a) => a.mediaType))]
                  .filter(Boolean)
                  .join(", ") || "unknown";
              toast.error(`Expected ${mediaType}, got ${got}`);
            }
            return;
          }
          const toValue = (a) =>
            a.thumbnail_url
              ? { url: a.url, thumbnail_url: a.thumbnail_url }
              : a.url;
          if (multiSelect) {
            const value = toValue(matching[0]);
            if (onAdd) onAdd([value]);
            else onChange?.(value);
          } else {
            onChange?.(toValue(matching[0]));
          }
        } catch {
          /* ignore */
        }
        return;
      }
      emitFiles(e.dataTransfer.files);
    },
    [
      acceptsDrag,
      mediaType,
      onChange,
      onAdd,
      multiSelect,
      resolveAssetById,
      onAssetDrop,
      emitFiles,
    ],
  );

  const handleUploadClick = useCallback(() => {
    if (disabled) return;
    // Defer so the dropdown finishes closing / restoring focus before the
    // native file dialog opens (otherwise some browsers swallow the click).
    setTimeout(() => document.getElementById(inputId)?.click(), 0);
  }, [disabled, inputId]);

  /**
   * Emits already-uploaded asset records (from AssetsBrowserModal) as picker
   * values, mirroring `emitFiles` but for remote assets. Rich
   * `{ url, thumbnail_url }` values are produced so thumbnails render.
   */
  const emitAssets = useCallback(
    (assetList) => {
      const valid = (assetList || []).filter(
        (a) => a?.mediaType === mediaType && a?.url,
      );
      if (valid.length === 0) return;
      const toValue = (a) =>
        a.thumbnail_url
          ? { url: a.url, thumbnail_url: a.thumbnail_url }
          : a.url;
      if (multiSelect) {
        const values = valid.map(toValue);
        if (onAdd) onAdd(values);
        else onChange?.(values[0]);
      } else {
        onChange?.(toValue(valid[0]));
      }
    },
    [mediaType, multiSelect, onAdd, onChange],
  );

  const handleBrowseClick = useCallback(async () => {
    if (disabled) return;
    const result = await openModal(
      <AssetsBrowserModal mediaType={mediaType} multiSelect={multiSelect} />,
    );
    if (!result) return;
    emitAssets(Array.isArray(result) ? result : [result]);
  }, [disabled, openModal, mediaType, multiSelect, emitAssets]);

  const handleFileChange = useCallback(
    (e) => {
      // Snapshot into an array *before* clearing the input: `e.target.files`
      // is a live FileList, and resetting `e.target.value` empties that same
      // object, so reading it after the reset would yield nothing.
      const files = Array.from(e.target.files || []);
      e.target.value = "";
      emitFiles(files);
    },
    [emitFiles],
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange?.(null);
    },
    [onChange],
  );

  const handlePreviewClick = useCallback(
    (e) => {
      e.stopPropagation();
      onPreview?.(asset, mediaType);
    },
    [onPreview, asset, mediaType],
  );

  const filled = !!asset;
  const displayUrl = pickerDisplayUrl(asset);
  const videoThumbnail = pickerThumbnailUrl(asset);

  const pickerSquare = (
    <div
      className={`relative shrink-0 ${sizeClass} rounded-md border bg-background overflow-hidden flex items-center justify-center select-none transition-opacity outline-hidden focus:outline-hidden focus-visible:outline-hidden
                ${filled ? "border-solid border-secondary" : "border-dashed border-secondary cursor-pointer hover:border-primary hover:text-primary"}
                ${isDragOver ? "border-primary border-solid bg-primary/10 ring-1 ring-primary/30" : ""}
                ${isDragActive && !isDragTypeMatch ? "opacity-30" : ""}
                ${filled ? "cursor-pointer" : ""}
            `}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={filled ? handlePreviewClick : undefined}
      {...(filled ? dragHandleProps : {})}>
      <input
        id={inputId}
        type="file"
        accept={`${mediaType}/*`}
        multiple={multiSelect}
        onChange={handleFileChange}
        className="hidden"
      />

      {filled && mediaType === "image" && (
        <img
          src={displayUrl}
          alt="asset"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover pointer-events-none"
        />
      )}
      {filled &&
        mediaType === "video" &&
        (videoThumbnail ? (
          <img
            src={videoThumbnail}
            alt="asset"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover pointer-events-none"
          />
        ) : (
          <video
            src={displayUrl}
            muted
            className="w-full h-full object-cover pointer-events-none"
          />
        ))}
      {filled && mediaType === "audio" && (
        <div className="w-full h-full flex items-center justify-center text-secondary-foreground/70 pointer-events-none">
          <LuAudioLines className="w-5 h-5" />
        </div>
      )}

      {!filled && <FiPlus className="w-5 h-5 text-secondary-foreground/60" />}

      {filled && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={handleClear}
          className="absolute top-0.5 right-0.5 p-1 rounded-sm bg-background cursor-pointer text-secondary-foreground/80 hover:text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity outline-hidden focus:outline-hidden focus-visible:outline-hidden"
          title="Clear">
          <FiTrash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );

  // Filled pickers click straight through to preview; empty pickers open a
  // small menu offering Upload (local file picker) or Browse (pick an
  // existing asset without leaving the current folder).
  if (filled) return pickerSquare;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        {pickerSquare}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-32">
        <DropdownMenuItem className="text-xs" onSelect={handleUploadClick}>
          <FiUpload className="w-3 h-3" />
          Upload
        </DropdownMenuItem>
        <DropdownMenuItem className="text-xs" onSelect={handleBrowseClick}>
          <FiFolder className="w-3 h-3" />
          Browse
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SortableMiniAssetPicker({ id, ...pickerProps }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} className="group relative">
      <MiniAssetPicker
        {...pickerProps}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

/**
 * Renders a horizontal sortable row of MiniAssetPicker items for an elements array,
 * plus a non-sortable "add" picker at the end (when under max).
 */
function ElementsRow({
  values,
  mediaType,
  max,
  onChange,
  onPreview,
  resolveAssetById,
  onAssetDrop,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const rowRef = useRef(null);
  const restrictToRow = useMemo(() => makeRestrictToContainer(rowRef), []);
  const modifiers = useMemo(
    () => [restrictToHorizontalAxis, restrictToRow, restrictToWindowEdges],
    [restrictToRow],
  );

  // Stable per-render ids: encode position + identity so duplicates don't
  // collide in a single render. Pending uploads use their previewUrl; rich
  // values use their canonical url; plain strings use themselves.
  const items = useMemo(
    () =>
      values.map((v, idx) => {
        const key = typeof v === "string" ? v : v?.previewUrl || v?.url || "";
        return { id: `${idx}::${key}`, value: v };
      }),
    [values],
  );
  const ids = useMemo(() => items.map((i) => i.id), [items]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove(values, oldIndex, newIndex));
  };

  return (
    <div className="flex items-center gap-2 px-[1px]">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={modifiers}>
        <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
          {/* `rowRef` wraps only the sortable items so the drag bounds
                        stop at the last existing picker and exclude the trailing
                        "+" add picker. Inner flex preserves the spacing between
                        the items themselves. */}
          {values.length > 0 && (
            <div ref={rowRef} className="flex items-center gap-2">
              {items.map((item, idx) => (
                <div key={item.id} className="group relative">
                  <SortableMiniAssetPicker
                    id={item.id}
                    asset={item.value}
                    mediaType={mediaType}
                    resolveAssetById={resolveAssetById}
                    onAssetDrop={onAssetDrop}
                    onPreview={onPreview}
                    onChange={(newValue) => {
                      if (newValue == null) {
                        onChange(values.filter((_, i) => i !== idx));
                        return;
                      }
                      // Drop-replace at this index, then collapse any
                      // resulting duplicates (keep first occurrence) so the
                      // same asset can't appear twice in the row.
                      const replaced = values.map((u, i) =>
                        i === idx ? newValue : u,
                      );
                      const seen = new Set();
                      const deduped = replaced.filter((v) => {
                        const k = pickerDedupeKey(v);
                        if (k == null) return true;
                        if (seen.has(k)) return false;
                        seen.add(k);
                        return true;
                      });
                      onChange(deduped);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </SortableContext>
      </DndContext>
      {values.length < max && (
        <div className="group relative">
          <MiniAssetPicker
            asset={null}
            mediaType={mediaType}
            resolveAssetById={resolveAssetById}
            onAssetDrop={onAssetDrop}
            multiSelect
            onAdd={(newValues) => {
              if (!newValues?.length) return;
              const deduped = dedupeAgainst(values, newValues);
              if (deduped.length === 0) return;
              const remaining = max - values.length;
              onChange([...values, ...deduped.slice(0, remaining)]);
            }}
            onChange={(newValue) => {
              if (!newValue) return;
              const deduped = dedupeAgainst(values, [newValue]);
              if (deduped.length === 0) return;
              onChange([...values, deduped[0]]);
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Self-contained generator panel:
 *  - Visual asset inputs in a horizontal row
 *  - Prompt textarea(s)
 *  - Settings row (model picker + dropdowns/toggles + Generate / Schedule)
 */
const AiModelInput = forwardRef(function AiModelInput(
  {
    preview,
    onPreviewChange,
    onTaskSubmitted,
    storageKey = "kagami-aimodelinput-selected-model",
    className = "",
    resolveAssetById,
    destinationFolder = null,
  },
  ref,
) {
  const { show } = useShow();
  const { openModal } = useModal();
  const { runTask } = useKagamiBridge();
  const { uploadFile } = useFileUpload();

  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelData, setModelData] = useState({});
  const [modelsDialogOpen, setModelsDialogOpen] = useState(false);
  const [totalGenerations, setTotalGenerations] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [characters, setCharacters] = useState([]);
  // When true the panel collapses to a small "Generate" pill anchored at the
  // same bottom-left corner. Clicking the pill restores the full panel.
  const [minimized, setMinimized] = useState(false);

  // -------- Resize (top-right handle) --------
  // The panel manages its own width/height in pixels. Width is initialized
  // from localStorage (or a sensible default) and clamped to the viewport;
  // height defaults to `auto` (content-driven) until the user resizes.
  const SIZE_STORAGE_KEY = "kagami-aimodelinput-size";
  const MIN_WIDTH = 420;
  // Tiny safety floor; the real lower bound is computed dynamically from the
  // panel's `min-content` height at the start of every resize (see
  // `startResize`), so resizing always stops exactly when the non-textarea
  // rows would start clipping rather than at a fixed value.
  const MIN_HEIGHT = 80;
  const VIEWPORT_PADDING = 32;
  // Below this container width the panel stops being a fixed-width floating
  // card and instead fills the container (minus `COMPACT_PADDING` on each
  // side); horizontal resizing is disabled in this mode (height only).
  const COMPACT_BREAKPOINT = 700;
  const COMPACT_PADDING = 16;
  const rootRef = useRef(null);
  // Settings/actions row (model picker, settings, Generate button) — the
  // sole driver of the panel's minimum width (see `startResize`).
  const settingsRowRef = useRef(null);

  // Track the width of the positioned container the panel is anchored to so
  // we can flip into the compact (full-width) layout on narrow screens.
  const [containerWidth, setContainerWidth] = useState(null);
  useEffect(() => {
    if (minimized) return;
    const container = rootRef.current?.parentElement?.offsetParent;
    if (!container) return;
    const update = () => setContainerWidth(container.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(container);
    return () => ro.disconnect();
  }, [minimized]);

  const isCompact =
    containerWidth != null && containerWidth < COMPACT_BREAKPOINT;
  // Mirror to a ref so the (stable) resize handler reads the latest value.
  const isCompactRef = useRef(isCompact);
  isCompactRef.current = isCompact;
  const [size, setSize] = useState(() => {
    if (typeof window === "undefined") return { width: 900, height: null };
    try {
      const saved = JSON.parse(
        localStorage.getItem(SIZE_STORAGE_KEY) || "null",
      );
      if (saved && typeof saved.width === "number") {
        return {
          width: Math.min(saved.width, window.innerWidth - VIEWPORT_PADDING),
          height: typeof saved.height === "number" ? saved.height : null,
        };
      }
    } catch {
      /* ignore */
    }
    return {
      width: Math.min(900, window.innerWidth - VIEWPORT_PADDING),
      height: null,
    };
  });

  // Re-clamp width if the viewport shrinks below the persisted width.
  useEffect(() => {
    const onResize = () => {
      setSize((prev) => {
        const maxW = window.innerWidth - VIEWPORT_PADDING;
        const maxH = window.innerHeight - VIEWPORT_PADDING;
        const w = Math.min(prev.width, maxW);
        const h = prev.height == null ? null : Math.min(prev.height, maxH);
        if (w === prev.width && h === prev.height) return prev;
        return { width: w, height: h };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const startResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const root = rootRef.current;
    if (!root) return;

    // Measure the panel's *true* minimum height by forcing `min-content`
    // height. Because the textarea row has `min-h-0` and is shrinkable,
    // this collapses it to 0 vertically and keeps only the non-shrinkable
    // rows. Reads are synchronous so no flicker is painted.
    const savedHeight = root.style.height;
    root.style.height = "min-content";
    const intrinsicMinH = root.offsetHeight;
    root.style.height = savedHeight;

    // Minimum width comes solely from the settings/actions row (model
    // picker + settings + Generate button) — that row uses `flex` (no
    // wrap) so its min-content width equals the sum of its children. The
    // visual-inputs row (images/videos/audio) is intentionally excluded:
    // it scrolls horizontally instead of setting a floor on the panel's
    // width (see its `overflow-x-auto` in the JSX below).
    const settingsRow = settingsRowRef.current;
    let intrinsicMinW = MIN_WIDTH;
    if (settingsRow) {
      const savedRowWidth = settingsRow.style.width;
      settingsRow.style.width = "min-content";
      const rowMinContent = settingsRow.offsetWidth;
      settingsRow.style.width = savedRowWidth;
      const rootStyle = window.getComputedStyle(root);
      const horizontalChrome =
        parseFloat(rootStyle.paddingLeft || "0") +
        parseFloat(rootStyle.paddingRight || "0") +
        parseFloat(rootStyle.borderLeftWidth || "0") +
        parseFloat(rootStyle.borderRightWidth || "0");
      intrinsicMinW = rowMinContent + horizontalChrome;
    }

    const rect = root.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = rect.width;
    const startH = rect.height;
    const maxW = window.innerWidth - VIEWPORT_PADDING;
    const maxH = window.innerHeight - VIEWPORT_PADDING;
    const minH = Math.max(MIN_HEIGHT, intrinsicMinH);
    const minW = Math.max(MIN_WIDTH, intrinsicMinW);

    // Detect whether the panel's wrapper centers it horizontally (e.g.
    // `left-1/2 -translate-x-1/2`). When centered, growing the width by
    // ΔW only moves the right edge by ΔW/2, so the mouse outruns the
    // handle. We compensate by doubling the dx in that case so the right
    // edge tracks the cursor 1:1.
    //
    // We detect centering by comparing the wrapper's layout position
    // (`offsetLeft`, transforms ignored) with its visual position
    // (`getBoundingClientRect().left`, transforms applied). A negative
    // delta of ~half the wrapper width means a `-translate-x-1/2` is in
    // effect — which works whether Tailwind emits it via the legacy
    // `transform` or the new `translate` CSS property.
    const wrapper = root.parentElement;
    let widthMultiplier = 1;
    if (wrapper) {
      const wRect = wrapper.getBoundingClientRect();
      const offsetParent = wrapper.offsetParent;
      const opRect = offsetParent?.getBoundingClientRect();
      const layoutLeft = opRect ? opRect.left + wrapper.offsetLeft : wRect.left;
      const xTranslate = wRect.left - layoutLeft;
      if (Math.abs(xTranslate + wRect.width / 2) < 2) widthMultiplier = 2;
    }

    // In compact mode the width is driven by the container, so the handle
    // only adjusts height (vertical resize).
    const compact = isCompactRef.current;

    let latest = { width: startW, height: startH };
    const onMove = (ev) => {
      // Top-right handle: dragging right widens (+dx, scaled by the
      // wrapper-anchoring multiplier), dragging up increases height
      // (-dy) since the panel is anchored at the bottom by the parent.
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      latest = {
        width: compact
          ? startW
          : Math.max(minW, Math.min(maxW, startW + widthMultiplier * dx)),
        height: Math.max(minH, Math.min(maxH, startH - dy)),
      };
      setSize(latest);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      try {
        localStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(latest));
      } catch {
        /* ignore */
      }
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = compact ? "ns-resize" : "ne-resize";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  // -------- Fetch models --------
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await apiFetch("/api/models");
        if (!res.ok) throw new Error("Failed to fetch models");
        const data = await res.json();
        if (cancelled) return;
        setModels(data);
        const savedId = localStorage.getItem(storageKey);
        const initial =
          (savedId && data.find((m) => m._id === savedId)) || data[0];
        if (initial) {
          setSelectedModel(initial);
          initModelData(initial);
        }
      } catch (err) {
        console.error("Error fetching models:", err);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initModelData = (model) => {
    const md = {};
    if (model?.inputs?.length) {
      model.inputs.forEach((input) => {
        md[input.prop] = input.default;
      });
    }
    setModelData(md);
  };

  // -------- Persist selection / re-init data on model change --------
  useEffect(() => {
    if (!selectedModel) return;
    localStorage.setItem(storageKey, selectedModel._id);
    setModelData((prev) => {
      const md = {};
      selectedModel.inputs?.forEach((input) => {
        const raw = prev[input.prop];
        const existing = raw === "" || raw == null ? undefined : raw;
        let value;
        if (input.type === "options" && Array.isArray(input.enum)) {
          value = input.enum.some((e) => e.key === existing)
            ? existing
            : input.default;
        } else {
          value = existing ?? input.default;
        }
        if (value !== undefined) md[input.prop] = value;
      });
      return md;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel?._id]);

  // -------- Fetch characters when needed --------
  const hasCharactersInput = !!selectedModel?.inputs?.some(
    (i) => i.type === "character",
  );
  useEffect(() => {
    if (!hasCharactersInput || !show?._id) return;
    const fetchCharacters = async () => {
      try {
        const res = await apiFetch(`/api/shows/${show._id}/characters`);
        if (!res.ok) throw new Error("Failed to fetch characters");
        const data = await res.json();
        setCharacters(
          data
            .filter((c) => c.voice_ids?.length > 0)
            .map((c) => ({
              name: c.name,
              voiceId: c.voice_ids[0].voice_id,
              thumbnail_url: c.thumbnail_url,
            })),
        );
      } catch (err) {
        console.error("Error fetching characters:", err);
      }
    };
    fetchCharacters();
  }, [hasCharactersInput, show?._id]);

  // -------- Imperative handle: recall settings from an asset/generation --------
  // Lets parents (e.g. AssetsEditor) preset the panel from a previous task:
  //  - Expand if currently minimized
  //  - Select the matching model (looked up by id in the already-loaded list)
  //  - Apply the previously-used settings as modelData (the model-change
  //    effect uses `setModelData(prev => ...)` so the recalled values are
  //    preserved over each input's `default`)
  // Holds a recall request whose model hasn't loaded yet (e.g. the panel was
  // mounted at the same time the request arrived). Applied by the effect below
  // once `models` is populated.
  const pendingRecallRef = useRef(null);

  const applyRecall = useCallback((model, settings) => {
    if (model) setSelectedModel(model);
    if (settings && typeof settings === "object") setModelData({ ...settings });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      recallSettings: ({ modelId, settings }) => {
        setMinimized(false);
        const model = modelId ? models.find((m) => m._id === modelId) : null;
        // Model id given but not loaded yet — stash and apply when models arrive.
        if (modelId && !model) {
          pendingRecallRef.current = { modelId, settings };
          return;
        }
        applyRecall(model, settings);
      },
    }),
    [models, applyRecall],
  );

  // Flush a deferred recall once the matching model becomes available.
  useEffect(() => {
    const pending = pendingRecallRef.current;
    if (!pending || models.length === 0) return;
    const model = models.find((m) => m._id === pending.modelId);
    if (!model) return;
    pendingRecallRef.current = null;
    applyRecall(model, pending.settings);
  }, [models, applyRecall]);

  // -------- Helpers --------
  const updateModelData = (key, value) =>
    setModelData((prev) => ({ ...prev, [key]: value }));

  const isPickerValueFilled = (v) => {
    if (isPendingUpload(v)) return true;
    if (typeof v === "string") return v.trim() !== "";
    return !!pickerSubmitUrl(v);
  };

  const isValueFilled = (value, type) => {
    if (value === null || value === undefined) return false;
    switch (type) {
      case "image":
      case "video":
      case "audio":
        return isPickerValueFilled(value);
      case "text":
      case "options":
      case "character":
        return String(value).trim() !== "";
      case "elements":
      case "video_elements":
      case "audio_elements":
        return (
          Array.isArray(value) &&
          value.length > 0 &&
          value.every(isPickerValueFilled)
        );
      case "boolean":
        return typeof value === "boolean";
      case "voice_settings":
        return value != null && typeof value === "object";
      default:
        return true;
    }
  };

  const requiredFieldsFilled = () => {
    const inputs = selectedModel?.inputs;
    if (!inputs?.length) return true;
    const req = inputs.filter((i) => i.required === true);
    if (!req.length) return true;
    return req.every((i) => isValueFilled(modelData?.[i.prop], i.type));
  };

  /**
   * Walk modelData and replace any pending-upload values with the URLs from
   * actually uploading them now. We cache per-File results so the same File
   * referenced from multiple inputs only uploads once.
   */
  const uploadPendingAssets = async (data) => {
    const inputs = selectedModel?.inputs || [];
    const uploadCache = new Map();
    const upload = async (pending) => {
      if (uploadCache.has(pending.file)) return uploadCache.get(pending.file);
      const promise = uploadFile(pending.file, "temp").then((res) => {
        if (!res?.url) throw new Error("Upload failed");
        return res.url;
      });
      uploadCache.set(pending.file, promise);
      return promise;
    };
    // Rich `{ url, thumbnail_url }` values get flattened to a plain URL
    // string for submission; pending uploads are resolved by uploading.
    const flatten = async (v) => {
      if (isPendingUpload(v)) return upload(v);
      if (typeof v === "string") return v;
      return pickerSubmitUrl(v);
    };
    const result = { ...data };
    for (const input of inputs) {
      const v = result[input.prop];
      if (v == null) continue;
      if (
        input.type === "image" ||
        input.type === "video" ||
        input.type === "audio"
      ) {
        result[input.prop] = await flatten(v);
      } else if (
        input.type === "elements" ||
        input.type === "video_elements" ||
        input.type === "audio_elements"
      ) {
        if (!Array.isArray(v)) continue;
        const next = [];
        for (const item of v) next.push(await flatten(item));
        result[input.prop] = next;
      }
    }
    return result;
  };

  const handleSubmit = async (schedule = false) => {
    if (!selectedModel || !show?._id) return;
    let run_at = null;
    if (schedule) {
      const dt = await openModal(<ScheduleModal />);
      if (!dt) return;
      run_at = dt;
    }
    try {
      setSubmitting(true);
      const resolvedPayload = await uploadPendingAssets(modelData);
      const groupId = crypto.randomUUID();
      for (let i = 0; i < totalGenerations; i++) {
        await runTask({
          modelId: selectedModel._id,
          payload: resolvedPayload,
          showId: show._id,
          group: groupId,
          generate_assets: true,
          destination_folder: destinationFolder || null,
          ...(run_at && { run_at }),
        });
      }
      onTaskSubmitted?.();
    } catch (err) {
      console.error("Error submitting task:", err);
      toast.error("Failed to submit task");
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Opens the right preview modal for a picker value. Already-uploaded URL
   * strings go through the rich AssetPreviewModal; pending (local) files use
   * the lightweight Image/Video/AudioPreviewModal so we don't need a full
   * asset record (which we don't have until upload).
   */
  const handlePreviewPickerValue = (value, mediaType) => {
    if (isPendingUpload(value)) {
      const asset = {
        url: value.previewUrl,
        thumbnail_url: value.previewUrl,
        name: value.name || "",
        mediaType,
      };
      if (mediaType === "video") openModal(<VideoPreviewModal video={asset} />);
      else if (mediaType === "audio")
        openModal(<AudioPreviewModal audio={asset} />);
      else openModal(<ImagePreviewModal image={asset} />);
      return;
    }
    const url = pickerSubmitUrl(value);
    if (!url) return;
    const asset = {
      url,
      thumbnail_url: pickerDisplayUrl(value) || url,
      name: "",
      mediaType,
    };
    openModal(<AssetPreviewModal asset={asset} />);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit(false);
    }
  };

  // -------- Categorize inputs --------
  const inputs = selectedModel?.inputs || [];
  const visualInputs = inputs.filter((i) => VISUAL_TYPES.includes(i.type));
  const textInputs = inputs.filter((i) => i.type === "text");
  const settingInputs = inputs.filter((i) => SETTING_TYPES.includes(i.type));

  /**
   * Routes a multi-asset drop across all visual inputs by mediaType.
   *
   * - The picker that physically received the drop (`targetInput`) gets first
   *   pick of any assets matching its own type — replacing single-value
   *   pickers and appending to elements-style pickers.
   * - Every remaining asset is then placed into the *best* input for its
   *   mediaType: elements arrays are preferred (since they hold many),
   *   falling back to a same-type single picker only if that slot is empty.
   * - Assets with no matching input on the current model are reported via a
   *   toast so the user knows nothing happened to them.
   */
  const distributeAssetsToInputs = useCallback(
    (draggedAssets, targetInput) => {
      const toValue = (a) =>
        a.thumbnail_url
          ? { url: a.url, thumbnail_url: a.thumbnail_url }
          : a.url;

      setModelData((prev) => {
        const updates = { ...prev };
        const consumed = new Set();
        const handled = new Set();

        const writeToInput = (input, assets, { replace }) => {
          const mt = elementsMediaType(input.type);
          const matching = assets.filter(
            (a) => a?.mediaType === mt && a?.url && !consumed.has(a),
          );
          if (matching.length === 0) return;
          if (ELEMENTS_INPUT_TYPES.has(input.type)) {
            const current = Array.isArray(updates[input.prop])
              ? updates[input.prop]
              : [];
            const seen = new Set(
              current.map(pickerDedupeKey).filter((k) => k != null),
            );
            const max = input.max || 8;
            const remaining = Math.max(0, max - current.length);
            const taken = [];
            for (const a of matching) {
              if (taken.length >= remaining) break;
              if (seen.has(a.url)) continue;
              seen.add(a.url);
              taken.push(a);
            }
            if (taken.length === 0) return;
            updates[input.prop] = [...current, ...taken.map(toValue)];
            taken.forEach((a) => consumed.add(a));
          } else {
            if (!replace && isPickerValueFilled(updates[input.prop])) return;
            updates[input.prop] = toValue(matching[0]);
            consumed.add(matching[0]);
          }
        };

        if (targetInput && VISUAL_TYPES.includes(targetInput.type)) {
          writeToInput(targetInput, draggedAssets, { replace: true });
          handled.add(targetInput.prop);
        }

        const remainingTypes = [
          ...new Set(
            draggedAssets
              .filter((a) => !consumed.has(a))
              .map((a) => a?.mediaType)
              .filter(Boolean),
          ),
        ];
        for (const mt of remainingTypes) {
          const group = draggedAssets.filter(
            (a) => a.mediaType === mt && !consumed.has(a),
          );
          const elementsType = ELEMENTS_INPUT_TYPE_FOR[mt];
          const elementsInput = elementsType
            ? visualInputs.find(
                (i) => i.type === elementsType && !handled.has(i.prop),
              )
            : null;
          const singleInput = visualInputs.find(
            (i) => i.type === mt && !handled.has(i.prop),
          );
          const best = elementsInput || singleInput;
          if (!best) continue;
          writeToInput(best, group, { replace: false });
          handled.add(best.prop);
        }

        return updates;
      });
    },
    [visualInputs],
  );

  const renderVisualInput = (input) => {
    if (
      input.type === "image" ||
      input.type === "video" ||
      input.type === "audio"
    ) {
      return (
        <div
          id={`AiModelInput__visualInput-${input.prop}`}
          key={input.prop}
          className="flex flex-col gap-1"
          title={input.name}>
          <span className="sticky left-0 z-10 self-start text-[10px] text-secondary-foreground/70 px-0.5 select-none truncate max-w-20">
            {input.name}
            {input.required && (
              <span className="text-destructive-foreground">*</span>
            )}
          </span>
          <div className="group relative">
            <MiniAssetPicker
              asset={modelData?.[input.prop]}
              mediaType={input.type}
              resolveAssetById={resolveAssetById}
              onAssetDrop={(assets) => distributeAssetsToInputs(assets, input)}
              onChange={(value) => updateModelData(input.prop, value)}
              onPreview={handlePreviewPickerValue}
            />
          </div>
        </div>
      );
    }
    const mt = elementsMediaType(input.type);
    const values = Array.isArray(modelData?.[input.prop])
      ? modelData[input.prop]
      : [];
    return (
      <div
        id={`AiModelInput__visualInput-${input.prop}`}
        key={input.prop}
        className="flex flex-col gap-1"
        title={input.name}>
        <span className="sticky left-0 z-10 self-start text-[10px] text-secondary-foreground/70 px-0.5 select-none truncate">
          {input.name}
          {input.required && (
            <span className="text-destructive-foreground">*</span>
          )}
        </span>
        <ElementsRow
          values={values}
          mediaType={mt}
          max={input.max || 8}
          onChange={(next) => updateModelData(input.prop, next)}
          onPreview={handlePreviewPickerValue}
          resolveAssetById={resolveAssetById}
          onAssetDrop={(assets) => distributeAssetsToInputs(assets, input)}
        />
      </div>
    );
  };

  const renderSettingInput = (input) => {
    const value = modelData?.[input.prop];
    if (input.type === "options") {
      const icon = getOptionsIcon(input.name);
      return (
        <div key={input.prop} className="flex flex-col gap-1">
          <Select
            value={value}
            onValueChange={(v) => updateModelData(input.prop, v)}>
            <SelectTrigger
              id={`AiModelInput__setting-${input.prop}`}
              className="text-xs rounded-sm border-0 !h-6 px-2 select-none gap-1.5"
              showChevron={false}>
              {icon && (
                <span className="text-secondary-foreground/70 shrink-0">
                  {icon}
                </span>
              )}
              <SelectValue placeholder={input.name} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {input.enum?.map((opt) => (
                  <SelectItem
                    id={`AiModelInput__settingOption-${input.prop}-${opt.key}`}
                    key={opt.key}
                    value={opt.key}
                    className="text-xs">
                    {opt.value}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (input.type === "character") {
      const selectedCharacter = characters.find((c) => c.voiceId === value);
      const showThumbnail = !!selectedCharacter?.thumbnail_url;
      return (
        <div key={input.prop} className="flex flex-col gap-1">
          <Select
            value={value}
            onValueChange={(v) => updateModelData(input.prop, v)}>
            <SelectTrigger
              size="xs"
              className="min-w-24 text-xs select-none px-1.5 flex items-center justify-start"
              showChevron={false}>
              {showThumbnail ? (
                <img
                  src={selectedCharacter.thumbnail_url}
                  alt={selectedCharacter.name}
                  className="w-3 h-3 rounded-full object-cover shrink-0"
                />
              ) : (
                <span className="text-secondary-foreground/70 shrink-0">
                  <LuUser className="w-3 h-3 shrink-0" />
                </span>
              )}
              <SelectValue placeholder="Character" className="text-xs">
                {selectedCharacter?.name}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {characters.map((c) => (
                  <SelectItem
                    key={c.voiceId}
                    value={c.voiceId}
                    className="text-xs">
                    {c.thumbnail_url ? (
                      <img
                        src={c.thumbnail_url}
                        alt={c.name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    ) : (
                      <LuUser className="w-6 h-6 p-1 rounded-full bg-secondary text-secondary-foreground/70" />
                    )}
                    <span className="text-xs">{c.name}</span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (input.type === "number") {
      return (
        <div key={input.prop} className="flex flex-col gap-1">
          <span className="text-[10px] text-secondary-foreground/70 px-0.5 select-none">
            {input.name}
            {input.required && (
              <span className="text-destructive-foreground">*</span>
            )}
          </span>
          <Input
            type="number"
            min={input.min ?? -9999}
            max={input.max ?? 9999}
            step={input.step ?? 1}
            value={value ?? ""}
            onChange={(e) =>
              updateModelData(input.prop, parseFloat(e.target.value || "0"))
            }
            className="h-6 w-18 text-xs pr-0 overflow-hidden"
          />
        </div>
      );
    }
    if (input.type === "boolean") {
      return (
        <div
          key={input.prop}
          className="flex gap-1 items-center border rounded-sm h-6  px-2">
          <Checkbox
            id={input.prop}
            checked={!!value}
            onCheckedChange={(c) => updateModelData(input.prop, c)}
          />
          <label
            htmlFor={input.prop}
            className="text-[10px] text-secondary-foreground/70 px-0.5 select-none">
            {input.name}
          </label>
        </div>
      );
    }
    if (input.type === "voice_settings") {
      return (
        <div key={input.prop} className="flex flex-col gap-1">
          <Button
            type="secondary"
            size="sm"
            className="!text-xs !h-6"
            icon={<LuAudioLines className="w-3 h-3" />}
            onClick={async () => {
              const next = await openModal(
                <VoiceSettingsModal voiceSettings={value || {}} />,
              );
              if (next) updateModelData(input.prop, next);
            }}>
            {value ? "Edit" : "Settings"}
          </Button>
        </div>
      );
    }
    return null;
  };

  if (minimized) {
    return (
      <div className={`flex ${className}`}>
        <button
          id="AiModelInput__restore"
          type="button"
          onClick={() => setMinimized(false)}
          title="Generate"
          className="inline-flex items-center border gap-2 h-8 px-4 py-3 rounded-xl bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground text-sm font-medium shadow-lg cursor-pointer">
          <span>Generate</span>
          <RiAiGenerate2 className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <div
      id="AiModelInput"
      ref={rootRef}
      className={`relative flex flex-col gap-1 bg-card/95 backdrop-blur-sm border border-secondary rounded-2xl rounded-tl-none shadow-md ${className}`}
      style={{
        width: isCompact ? containerWidth - COMPACT_PADDING * 2 : size.width,
        height: size.height ?? undefined,
        // minWidth: isCompact ? 0 : 'min-content',
        minHeight: "min-content",
      }}>
      <div className="flex absolute bottom-full left-[-1px]">
        <div
          id="AiModelInput__minimize"
          role="button"
          tabIndex={0}
          title="Minimize"
          onClick={() => setMinimized(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setMinimized(true);
            }
          }}
          className="w-6 h-5 bg-secondary rounded-t-sm flex justify-center items-center cursor-pointer hover:bg-primary hover:text-primary-foreground select-none">
          <FiChevronDown className="w-3 h-3" />
        </div>
      </div>
      <div
        onMouseDown={startResize}
        title="Resize"
        className={`absolute top-0 right-0 z-20 stroke-secondary-foreground/60 hover:stroke-primary flex items-start justify-end pr-2 pt-2 ${isCompact ? "cursor-ns-resize" : "cursor-nesw-resize"}`}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          className="touch-none z-10">
          <path
            d="M1 1H7A10 10 0 0 1 17 11V17"
            strokeWidth="3"
            strokeLinecap="round"></path>
        </svg>
      </div>
      {/* Row 1: visual inputs (each label + asset picker(s) in a horizontal row).
                `min-w-0` lets this row shrink below its content width instead of
                setting a floor on the panel's width — combined with
                `overflow-x-auto` it scrolls horizontally once the panel is
                narrower than the row's content. */}
      {visualInputs.length > 0 && (
        <div className="flex items-start gap-3 p-3 pb-2 overflow-x-auto min-w-0">
          {visualInputs.map(renderVisualInput)}
        </div>
      )}

      {/* Row 2: prompt(s). `flex-1 min-h-0` so the textarea absorbs any
                extra vertical space when the user resizes the panel upward. */}
      {textInputs.map((input) => (
        <div
          key={input.prop}
          className={`flex flex-1 min-h-0 flex-col gap-1 px-3 ${!visualInputs.length ? "pt-3" : ""}`}>
          {textInputs.length > 1 && (
            <span className="text-[10px] text-secondary-foreground/70 px-0.5 select-none">
              {input.name}
              {input.required && (
                <span className="text-destructive-foreground">*</span>
              )}
            </span>
          )}
          <textarea
            id={`AiModelInput__prompt-${input.prop}`}
            value={modelData?.[input.prop] ?? ""}
            onChange={(e) => updateModelData(input.prop, e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your prompt here & let's create something amazing..."
            className="placeholder:text-secondary-foreground/30 text-secondary-foreground text-xs/4 font-mono outline-hidden resize-none min-h-0 w-full flex-1"
            rows={3}
          />
        </div>
      ))}

      {/* Row 3: settings + actions */}
      {/* No `flex-wrap`: keeping this row on a single line means its
                min-content width drives the panel's MIN_WIDTH (measured at
                resize-start via `settingsRowRef`), so the user can't drag the
                panel narrower than the settings + Generate button need. */}
      <div ref={settingsRowRef} className="flex items-end gap-2 px-3 pb-3">
        <div className="flex gap-2 flex-1 items-end">
          <button
            id="AiModelInput__modelTrigger"
            type="button"
            onClick={() => setModelsDialogOpen(true)}
            className="min-w-32 inline-flex items-center gap-1 h-6 px-2 rounded-sm border bg-secondary hover:bg-secondary/80 text-xs text-secondary-foreground cursor-pointer max-w-60 truncate">
            <span className="truncate">
              {selectedModel?.name || "Select model"}
            </span>
          </button>

          {settingInputs.map(renderSettingInput)}
        </div>

        <div
          className={`flex ${submitting || !selectedModel || !requiredFieldsFilled() ? "opacity-60 pointer-events-none" : ""}`}>
          <Button
            id="AiModelInput__generate"
            type="primary"
            className="rounded-r-none border-0 h-6 rounded-sm"
            onClick={() => handleSubmit(false)}>
            {submitting ? "Submitting..." : "Generate"}
          </Button>
          {selectedModel?.max_generations > 1 && (
            <Select
              value={String(totalGenerations)}
              onValueChange={(v) => setTotalGenerations(parseInt(v, 10))}>
              <SelectTrigger
                id="AiModelInput__generations"
                className="rounded-l-none rounded-r-sm border-0 !h-6 text-xs select-none bg-primary text-primary-foreground"
                showChevron={false}>
                <SelectValue placeholder="1" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Array.from({ length: 4 }, (_, i) => i + 1).map((i) => (
                    <SelectItem
                      id={`AiModelInput__generationsOption-${i}`}
                      key={i}
                      value={String(i)}
                      className="text-xs">
                      {i}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
          {/* <Button
                        type='primary'
                        className='rounded-l-none border-0 h-8 px-2'
                        onClick={() => handleSubmit(true)}
                        disabled={submitting || !selectedModel || !requiredFieldsFilled()}
                        title='Schedule'
                    >
                        <FiCalendar className='w-4 h-4' />
                    </Button> */}
        </div>
      </div>

      <ModelSelectorDialog
        open={modelsDialogOpen}
        onOpenChange={setModelsDialogOpen}
        models={models}
        selectedModel={selectedModel}
        onSelectModel={setSelectedModel}
      />
    </div>
  );
});

export default AiModelInput;
